import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { PaymentStatusPreSubmit, PAYMENT_COMPS } from 'src/app/core';
import { ResponsiveService } from 'src/app/feature';
import { Step } from 'src/app/feature/payment/utilities';
import {
  Order,
  OrderDto,
  PaymentCreateDto,
  PaymentEntity,
  PaymentStepperNewEvent,
} from 'src/app/shared';
import { SubSink } from 'subsink';
import { OrderService } from 'src/app/feature';
import { ShoppingCartService } from 'src/app/feature/payment/services/shopping-cart.service';

@Component({
  selector: 'shopping-cart-select-orders',
  templateUrl: './select-orders.component.html',
  styleUrls: ['./select-orders.component.scss'],
})
export class SelectOrdersComponent
  extends Step<PaymentStepperNewEvent>
  implements OnInit
{
  @Output('stepper') stepper: EventEmitter<PaymentStepperNewEvent> =
    new EventEmitter();
  @Input() orders: Order[] = [];

  private subsink = new SubSink();
  placeholderLoading: boolean = true;

  public get paymentStatus(): Observable<PaymentStatusPreSubmit | undefined> {
    return this.shoppingCartService.paymentStatus$.pipe();
  }

  public get isMobile(): Observable<boolean> {
    return this.responsiveService.isMobile$;
  }

  public get selectedOrders(): Observable<Order[]> {
    return this.shoppingCartService.selectedOrdersList$;
  }

  // Constructors --------------------------------------->

  constructor(
    private shoppingCartService: ShoppingCartService,
    private responsiveService: ResponsiveService,
    private orderService: OrderService,
    private snakeBar: MatSnackBar
  ) {
    super(PAYMENT_COMPS.SELECT_ORDERS);
  }

  ngOnInit(): void {
    // When submit btn clicked.
    this.subsink.sink = this.shoppingCartService.status$.subscribe(
      (event: any) => {
        if (event.name === 'submit') {
          this.submit();
        }
      }
    );
  }

  // Step methods
  next(to: number): void {}
  previous(): void {}

  // Public methods --------------------------------------->

  public remove(i: number) {
    const order = this.getOrder(i);
    const key = this.getOrderKey(order);

    if (!key) return;

    this.orderService
      .remove({ key })
      .then((result) => {
        if (result) {
          this.removeOrder(i);
        } else {
          this.onOperationError('متآسفانه مشکلی هنگام انجام عملیات پیش آمد');
        }
      })
      .catch((error) => {
        this.onOperationError(
          'متآسفانه مشکلی هنگام انجام عملیات پیش آمد',
          error
        );
      });
  }

  public select(order: Order) {
    this.shoppingCartService.selectOrder(order);
  }

  public disSelect(order: Order) {
    this.shoppingCartService.disSelectOrder(order);
  }

  public submit() {
    try {
      const dto = this.generatePaymentDto();
      if (!dto) throw new Error('Error in generating payment dto');
      this.createPayment(dto);
    } catch (err) {
      console.error(err);
    }
  }

  public createPayment(paymentCreateDto: PaymentCreateDto) {
    this.shoppingCartService
      .createPayment(paymentCreateDto)
      .subscribe((payment: PaymentEntity) => {
        this.stepper.emit(this.generateEvent('next', payment));
      });
  }

  public generateStatus(): PaymentStatusPreSubmit {
    return {
      price: (() => {
        const selectedOrders = this.orders.filter((item) => item.selected);
        const selectedOrdersLen = selectedOrders.length;

        if (selectedOrdersLen === 0) {
          return 0;
        }

        if (selectedOrders.length === 1) {
          return selectedOrders[0]?.price;
        }

        return selectedOrders
          .map((item) => +item.price)
          .reduce((a, b) => {
            return a + b;
          });
      })(),
      selectedCount: this.orders.filter((item) => {
        return item?.selected;
      }).length,
    };
  }

  // Private methods --------------------------------------->

  private generatePaymentDto(): PaymentCreateDto | void {
    return { orderIds: this.getSelectedOrdersId() };
  }

  private getSelectedOrdersId(): number[] {
    return this.orders.filter((item) => item?.selected).map((item) => item.id);
  }

  private onOperationError(msg: string, error?: any) {
    this.snakeBar
      .open(msg, 'رفرش', { duration: 1000 })
      .afterDismissed()
      .subscribe(window.location.reload);

    throw new error() || 'undefined error';
  }

  private removeOrder(index: number) {
    this.orders.splice(index, 1);
  }

  private getOrderKey(order: Order | OrderDto): number | undefined {
    if (order.id) return order.id;
    if ('localHash' in order) return order.localHash;

    console.error(order);
    throw new Error('[getOrderKey]: Order key not defined');
  }

  private getOrder(index: number) {
    return this.orders[index];
  }
}
